/**
 * Shared modal chrome for the image editor's form dialogs.
 *
 * On portrait / narrow viewports the panel renders as a bottom sheet
 * (full-width, anchored to the bottom edge, rounded top) — the native
 * mobile modal idiom; it also rises above the on-screen keyboard so a
 * focused input in the dialog isn't hidden. On `sm:` and up it's the
 * familiar centered card, unchanged from the desktop layout.
 *
 * Full-screen workspaces (Liquify, Refine Edge, Color Range) and large
 * reference dialogs deliberately do NOT use this — they stay full-screen.
 *
 * Usage (custom `fixed inset-0` modals):
 *   <div className={MODAL_OVERLAY}>
 *     <div className={`bg-background shadow-xl ${MODAL_SHEET_CHROME} w-full sm:max-w-md ...`}>
 *
 * For dialogs built on the shared `@/components/ui/dialog` `DialogContent`,
 * pass DIALOG_SHEET_CLASS as its `className` (it merges via tailwind-merge,
 * so the bottom-sheet positioning overrides the centered defaults on mobile
 * and the `sm:` classes restore centering on desktop).
 */

/** Dimmed full-screen overlay that positions its single child panel:
 *  bottom on portrait (`items-end`), centered on `sm:`. */
export const MODAL_OVERLAY =
  'fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center';

/** Sheet-vs-card chrome for a custom modal's panel: rounded top + top/
 *  side borders on portrait, full rounded border on `sm:`. Compose with
 *  the dialog's own `bg-background`, width, max-w, max-h, layout and
 *  padding. */
export const MODAL_SHEET_CHROME =
  'rounded-t-xl border-t border-x border-border sm:rounded-lg sm:border';

/** `className` override for the shared `DialogContent` (Radix) — flips its
 *  centered card to a bottom sheet on portrait, restores centering at
 *  `sm:`. Relies on `DialogContent` merging className via tailwind-merge. */
export const DIALOG_SHEET_CLASS =
  'bottom-0 left-0 right-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-t-xl rounded-b-none ' +
  'sm:bottom-auto sm:left-[50%] sm:right-auto sm:top-[50%] sm:w-full sm:max-w-lg ' +
  'sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg';
